import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

export interface ProductDetailsListStyles {
    wrapper?: InjectableCss;
    productDetailsTittle?: InjectableCss;
    descriptionAccordion?: InjectableCss;
    list?: InjectableCss;
    listContainer?: InjectableCss;
    listDecription?: InjectableCss;
}

export const productDetailsListStyles: ProductDetailsListStyles = {
    wrapper: {
        css: css`
            margin: 20px 0px;
        `,
    },
    productDetailsTittle: {
        css: css`
            border-bottom: 0.2rem solid #fc3;
            box-sizing: border-box;
            display: inline-flex;
            font-weight: 600;
            color: #474747;
            font-size: 24px;
            text-transform: uppercase;
        `,
    },
    descriptionAccordion: {
        css: css`
            margin-bottom: 20px;
            border-top: 1px solid #ccc;
        `,
    },
    list: {
        css: css`
            margin-top: 10px;
            > ul {
                list-style: disc !important;
                padding-left: 20px;
                color: #474747;
            }
        `,
    },
    listContainer: {
        css: css`
            padding: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 10px;
                    `,
                    css`
                        padding: 10px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    listDecription: {
        css: css`
            color: #474747;
        `,
    },
};

const styles = productDetailsListStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasParentProductId & HasProduct;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => ({});

const ProductDetailsList: React.FC<Props> = ({ product, fields }) => {
    if (!product || !product.properties || typeof product.properties.ecoM_WEB_LONG_DESCRIPTION !== "string") {
        return null;
    }

    const [productList, setProductList] = React.useState([]);

    React.useEffect(() => {
        if (product && product.properties.ecoM_BULLET_LIST) {
            setProductList(JSON.parse(product.properties.ecoM_BULLET_LIST));
        }
    }, [product]);

    return (
        <>
            {productList.length > 0 ? (
                <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_specification">
                    <StyledWrapper {...styles.descriptionAccordion}>
                        <StyledWrapper>
                            <Typography {...styles.productDetailsTittle}>
                                {fields.title ? fields.title : "PRODUCT DETAILS"}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.listContainer}>
                            <StyledWrapper {...styles.listDecription}>
                                {product.properties.ecoM_WEB_LONG_DESCRIPTION}
                            </StyledWrapper>
                            <StyledWrapper {...styles.list}>
                                {productList?.map((list: any, index: number) => (
                                    <ul key={index}>
                                        <li>{list}</li>
                                    </ul>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            ) : null}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsList)),
    definition: {
        displayName: " Custom Product Details List",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title ",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
